import type { ApolloClient } from '@apollo/client/core'

import { FieldTaxonomyTermParent, LanguageId, TaxonomyTermFieldsFragment, TermsDocument } from '../graphql/generated'

export const TERM_VOCABULARIES = [
  'visible_tags',
  'event_info_type',
  'tocco_event_category',
  'tocco_supply_area',
  'tocco_target_group',
  'tocco_event_qualification',
  'cbz_course_category',
  'cbz_education',
  'cbz_region',
  'institution_sector',
  'event_category',
  'event_type',
] as const
export type TermVocabulary = (typeof TERM_VOCABULARIES)[number]

export interface Term {
  id: string
  name: string
  parent: string
}

export type Terms = Record<TermVocabulary, Term[]>

const createEmptyTerms = (): Terms =>
  TERM_VOCABULARIES.reduce(
    (acc, key) => {
      acc[key] = []
      return acc
    },
    {} as Record<TermVocabulary, Term[]>,
  )

const hasParentField = (
  term: TaxonomyTermFieldsFragment,
): term is TaxonomyTermFieldsFragment & { parent?: (FieldTaxonomyTermParent | null)[] | null } => 'parent' in term

const isParentField = (value: FieldTaxonomyTermParent | null | undefined): value is FieldTaxonomyTermParent =>
  Boolean(value)

const getTerms = async (
  client: ApolloClient<unknown> | null | undefined,
  vocabularies: TermVocabulary[],
  parents: string[],
  language: LanguageId,
): Promise<Terms> => {
  if (!client) {
    return createEmptyTerms()
  }

  const response = await Promise.allSettled(
    vocabularies.map(async (vocabulary) => {
      const { data } = await client.query({
        query: TermsDocument,
        variables: {
          vocabulary,
          parentId: parents,
          language,
          ...(vocabulary === 'institution_sector' ? { limit: 50 } : {}),
        },
      })
      if (!data) return null

      const termsForVocabulary =
        (data.taxonomyTermQuery?.entities as TaxonomyTermFieldsFragment[] | undefined)?.map((item) => {
          const parentField = hasParentField(item) ? item.parent?.find(isParentField) : undefined

          return {
            id: item.tid?.toString() ?? '',
            name: item.name ?? '',
            parent: parentField?.targetId != null ? parentField.targetId.toString() : '',
          }
        }) ?? []

      return [vocabulary, termsForVocabulary] as const
    }),
  )

  const sucessfulRequests = response.filter((res) => res.status === 'fulfilled') as {
    status: 'fulfilled'
    value: [TermVocabulary, Term[]]
  }[]
  const terms = sucessfulRequests
    .map((res) => res.value)
    .reduce((acc, cur) => ({ ...acc, [cur[0]]: cur[1] }), {} as Record<TermVocabulary, Term[]>)

  return terms
}

export const useTerms = async (vocabularies: TermVocabulary[], parents: string[] = ['0']) => {
  const { languageCODE } = useLocale()
  const { clients } = useApollo()

  const cacheKey = ['terms', ...vocabularies, ...parents].filter(Boolean).join('-')

  // Use useAsyncData for initial SSR data fetch
  const { data: initialTerms } = await useAsyncData(cacheKey, async () => {
    return await getTerms(clients?.default, vocabularies, parents, languageCODE.value as LanguageId)
  })

  const terms = ref(initialTerms.value ?? createEmptyTerms())
  const active = reactive(
    vocabularies.reduce(
      (acc, cur) => ({
        ...acc,
        [cur]: [],
      }),
      {} as Record<TermVocabulary, string[]>,
    ),
  )

  return { terms, active } as const
}
